import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// Load Sarabun font for PDF
const loadSarabunFont = async () => {
  try {
    //const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    const fontResponse = await fetch("/fonts/THSarabunNew.ttf");
    if (!fontResponse.ok) {
      throw new Error(`Failed to fetch Sarabun font: ${fontResponse.statusText}`);
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const VaccineFundingMonthlySummary = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    WarehouseID: "",
    MonthYear: "",
  });
  const [warehouses, setWarehouses] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [committeeData, setCommitteeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [vaccineDetailsMap, setVaccineDetailsMap] = useState(new Map());
    const generateMonthYearOptions = () => {
      const thaiMonths = [
        "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน",
        "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม",
        "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
      ];

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // 0-based → +1

      const options = [];
      for (let y = currentYear - 5; y <= currentYear + 1; y++) {
        for (let m = 1; m <= 12; m++) {
          if (y > currentYear || (y === currentYear && m > currentMonth)) {
            continue; // ข้ามเดือนในอนาคต
          }
          const value = `${y}-${String(m).padStart(2, "0")}`; // ใช้ส่ง API
          const label = `${thaiMonths[m - 1]} ${y + 543}`; // แสดงผล
          options.push({ value, label });
        }
      }
      return options.reverse(); // ให้เดือนล่าสุดอยู่ด้านบน
    };

  // Fetch warehouse options
  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineChildWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Warehouses Response:", response);
      if (response.result === "success") {
        const warehouseList = response.VaccineWarehousesList || [];
        setWarehouses(warehouseList);
        // Set default WarehouseID to the first warehouse, if available
        if (warehouseList.length > 0) {
          setFilterData((prev) => ({
            ...prev,
            WarehouseID: warehouseList[0].WarehouseID,
          }));
          console.log("Default WarehouseID set to:", warehouseList[0].WarehouseID);
        }
      } else {
        console.error("Failed to fetch warehouses:", response.error);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  // Fetch committee data
  const fetchCommitteeData = async (warehouseID) => {
    if (!warehouseID) return;
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeCommitteeByWarehouseID",
        data: { WarehouseID: warehouseID },
        sessionId: sessionId || "",
      });
      console.log("Committee Response:", response);
      if (response.result === "success") {
        setCommitteeData(response.Committee || null);
      } else {
        console.error("Failed to fetch committee data:", response.error);
        setCommitteeData(null);
      }
    } catch (error) {
      console.error("Error fetching committee data:", error);
      setCommitteeData(null);
    }
  };

  // Fetch all vaccine details
  const fetchVaccineDetails = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Vaccines List Response:", response);
      if (response.result === "success" && response.VaccinesList) {
        const detailsMap = new Map();
        response.VaccinesList.forEach((vaccine) => {
          detailsMap.set(vaccine.VaccineID, vaccine);
        });
        setVaccineDetailsMap(detailsMap);
        return detailsMap;
      }
      return new Map();
    } catch (error) {
      console.error("Error fetching vaccines list:", error);
      return new Map();
    }
  };

  // Fetch report data
  const fetchReportData = async () => {
    if (!filterData.WarehouseID) {
      alert("กรุณาเลือกคลังวัคซีน");
      return;
    }
    if (!filterData.MonthYear) {
      alert("กรุณาระบุเดือนและปี");
      return;
    }
    setIsLoading(true);
    try {
      // คำนวณ startDate และ endDate
      const [year, month] = filterData.MonthYear.split("-");
      const startDateObj = new Date(year, month - 1, 1);     // วันแรกของเดือน
      const endDateObj = new Date(year, month, 0);           // วันสุดท้ายของเดือน

      const startDate = formatDate(startDateObj);
      const endDate = formatDate(endDateObj);

      console.log("StartDate:", startDate);
      console.log("EndDate:", endDate);

      // Fetch committee data
      await fetchCommitteeData(filterData.WarehouseID);


      // Fetch vaccine details
      const vaccineMap = await fetchVaccineDetails();

      // Fetch carry-forward for specified month
      const carryForwardResponse = await apiService({
        method: "POST",
        url: "/getVaccineInventoryCarryForward",
        data: {
          year: year,
          month: month,
          WarehouseID: filterData.WarehouseID,
        },
        sessionId: sessionId || "",
      });
      console.log("CarryForward Response:", carryForwardResponse);

      // Fetch inbound
      const inboundResponse = await apiService({
        method: "POST",
        url: "/getVaccineInventoryInbound",
        data: {
          StartDate: startDate,
          EndDate: endDate,
          WarehouseID: filterData.WarehouseID,
        },
        sessionId: sessionId || "",
      });
      console.log("Inbound Response:", inboundResponse);

      // Fetch outbound
      const outboundResponse = await apiService({
        method: "POST",
        url: "/getVaccineInventoryOutbound",
        data: {
          StartDate: startDate,
          EndDate: endDate,
          WarehouseID: filterData.WarehouseID,
        },
        sessionId: sessionId || "",
      });
      console.log("Outbound Response:", outboundResponse);

      // Fetch disposal/return
      const disposalResponse = await apiService({
        method: "POST",
        url: "/getVaccineInventoryDisposal",
        data: {
          StartDate: startDate,
          EndDate: endDate,
          WarehouseID: filterData.WarehouseID,
        },
        sessionId: sessionId || "",
      });
      console.log("Disposal Response:", disposalResponse);

      if (
        carryForwardResponse.result === "success" &&
        inboundResponse.result === "success" &&
        outboundResponse.result === "success" &&
        disposalResponse.result === "success"
      ) {
        const data = await processReportData(
          carryForwardResponse.carryForwardList || [],
          inboundResponse.inboundList || [],
          outboundResponse.outboundList || [],
          disposalResponse.disposalList || disposalResponse.outboundList || [],
          vaccineMap
        );
        console.log("Processed Report Data:", data);
        setReportData(data);
      } else {
        setReportData([]);
        alert(
          "ไม่พบข้อมูล: " +
            (carryForwardResponse.error ||
              inboundResponse.error ||
              outboundResponse.error ||
              disposalResponse.error ||
              "เกิดข้อผิดพลาดในการดึงข้อมูล")
        );
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  // Process report data
  const processReportData = async (
    carryForward,
    inbound,
    outbound,
    disposal,
    vaccineMap
  ) => {
    const vaccineReportMap = new Map();

    // Initialize vaccine map with all vaccines that have activity
    const addVaccine = (VaccineID) => {
      if (!vaccineReportMap.has(VaccineID)) {
        vaccineReportMap.set(VaccineID, {
          VaccineID,
          VaccineNameTH: "",
          PackageSize: 1,
          PricePerDose: 0,
          CarryForwardDoses: 0,
          CarryForwardAmount: 0,
          InboundDoses: 0,
          InboundAmount: 0,
          OutboundDoses: 0,
          OutboundAmount: 0,
          DisposalDoses: 0,
          DisposalAmount: 0,
          EndingBalanceDoses: 0,
          EndingBalanceAmount: 0,
          Remark: "",
        });
      }
    };

    // Process carry-forward (convert bottles to doses)
    carryForward.forEach(({ VaccineID, CarryForwardQuantity }) => {
      addVaccine(VaccineID);
      const vaccine = vaccineReportMap.get(VaccineID);
      const details = vaccineMap.get(VaccineID);
      const packageSize = details?.PackageSize || 1;
      vaccine.PackageSize = packageSize;
      vaccine.CarryForwardDoses += (CarryForwardQuantity || 0) * packageSize;
    });

    // Process inbound (convert bottles to doses)
    inbound.forEach(({ VaccineID, "": ChangeQuantity }) => {
      addVaccine(VaccineID);
      const vaccine = vaccineReportMap.get(VaccineID);
      const details = vaccineMap.get(VaccineID);
      const packageSize = details?.PackageSize || 1;
      vaccine.PackageSize = packageSize;
      vaccine.InboundDoses += (ChangeQuantity || 0) * packageSize;
    });

    // Process outbound (convert bottles to doses)
    outbound.forEach(({ VaccineID, "": ChangeQuantity }) => {
      addVaccine(VaccineID);
      const vaccine = vaccineReportMap.get(VaccineID);
      const details = vaccineMap.get(VaccineID);
      const packageSize = details?.PackageSize || 1;
      vaccine.PackageSize = packageSize;
      vaccine.OutboundDoses += Math.abs(ChangeQuantity || 0) * packageSize;
    });

    // Process disposal/return (convert bottles to doses)
    disposal.forEach(({ VaccineID, ChangeQuantity, Remark }) => {
      addVaccine(VaccineID);
      const vaccine = vaccineReportMap.get(VaccineID);
      const details = vaccineMap.get(VaccineID);
      const packageSize = details?.PackageSize || 1;
      vaccine.PackageSize = packageSize;
      vaccine.DisposalDoses += Math.abs(ChangeQuantity || 0) * packageSize;
      if (Remark) {
        vaccine.Remark = vaccine.Remark
          ? `${vaccine.Remark}, ${Remark}`
          : Remark;
      }
    });

    // Set vaccine details and calculate amounts
    for (const [vaccineID, vaccine] of vaccineReportMap) {
      const details = vaccineMap.get(vaccineID);
      if (details) {
        vaccine.VaccineNameTH = details.VaccineNameTH || "ไม่ทราบชื่อ";
        vaccine.PackageSize = details.PackageSize || 1;
        vaccine.PricePerDose =
          details.PackageSize > 0
            ? (Number(details.PriceDistribution) || 0) / details.PackageSize
            : 0;
        vaccine.CarryForwardAmount =
          vaccine.CarryForwardDoses * vaccine.PricePerDose;
        vaccine.InboundAmount = vaccine.InboundDoses * vaccine.PricePerDose;
        vaccine.OutboundAmount = vaccine.OutboundDoses * vaccine.PricePerDose;
        vaccine.DisposalAmount = vaccine.DisposalDoses * vaccine.PricePerDose;
       vaccine.EndingBalanceDoses = (vaccine.CarryForwardDoses + vaccine.InboundDoses) - vaccine.OutboundDoses - vaccine.DisposalDoses;
       vaccine.EndingBalanceAmount = vaccine.EndingBalanceDoses * vaccine.PricePerDose;

      } else {
        vaccine.VaccineNameTH = "ไม่พบข้อมูลวัคซีน";
        vaccine.PricePerDose = 0;
        vaccine.CarryForwardAmount = 0;
        vaccine.InboundAmount = 0;
        vaccine.OutboundAmount = 0;
        vaccine.DisposalAmount = 0;
        vaccine.EndingBalanceDoses = 0;
        vaccine.EndingBalanceAmount = 0;
      }
    }

    const result = Array.from(vaccineReportMap.values()).filter(
      (vaccine) =>
        vaccine.CarryForwardDoses ||
        vaccine.InboundDoses ||
        vaccine.OutboundDoses ||
        vaccine.DisposalDoses
    );
    console.log("Final Vaccine Report Map:", result);
    return result;
  };

  useEffect(() => {
    if (sessionId) {
      fetchWarehouses();
    }
  }, [sessionId]);

  useEffect(() => {
    if (filterData.WarehouseID) {
      fetchCommitteeData(filterData.WarehouseID);
    }
  }, [filterData.WarehouseID, sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const formatNumber = (value, options = {}) => {
    const num = Number(value);
    return num === 0 ? "-" : num.toLocaleString("th-TH", options);
  };  

  // Table columns
  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ราคาต่อโด๊ส</span>
        </HStack>
      ),
      selector: (row) =>
        formatNumber(row.PricePerDose, { minimumFractionDigits: 2 }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ยอดยกมา (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.CarryForwardDoses),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ยอดยกมา (บาท)</span>
        </HStack>
      ),
      selector: (row) =>
        formatNumber(row.CarryForwardAmount, { minimumFractionDigits: 2 }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รับเข้ามา (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.InboundDoses),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รับเข้ามา (บาท)</span>
        </HStack>
      ),
      selector: (row) =>
        formatNumber(row.InboundAmount, { minimumFractionDigits: 2 }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำหน่าย (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.OutboundDoses),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำหน่าย (บาท)</span>
        </HStack>
      ),
      selector: (row) =>
        formatNumber(row.OutboundAmount, { minimumFractionDigits: 2 }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>แตก/ชำรุด (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.DisposalDoses),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>แตก/ชำรุด (บาท)</span>
        </HStack>
      ),
      selector: (row) =>
        formatNumber(row.DisposalAmount, { minimumFractionDigits: 2 }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ยอดยกไป (โด๊ส)</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.EndingBalanceDoses),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ยอดยกไป (บาท)</span>
        </HStack>
      ),
      selector: (row) =>
        formatNumber(row.EndingBalanceAmount, { minimumFractionDigits: 2 }),
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>หมายเหตุ</span>
        </HStack>
      ),
      selector: (row) => row.Remark || "-",
      sortable: false,
      width: "150px",
    },
  ];

  // Export to Excel
  const exportToExcel = () => {
    const wsData = [
      ...reportData.map((item) => ({
        "รหัสวัคซีน": item.VaccineID,
        "ชื่อวัคซีน": item.VaccineNameTH,
        "ราคาต่อโด๊ส": Number(item.PricePerDose).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
        }),
        "ยอดยกมา (โด๊ส)": item.CarryForwardDoses,
        "ยอดยกมา (บาท)": Number(item.CarryForwardAmount).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
        }),
        "รับเข้ามา (โด๊ส)": item.InboundDoses,
        "รับเข้ามา (บาท)": Number(item.InboundAmount).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
        }),
        "จำหน่าย (โด๊ส)": item.OutboundDoses,
        "จำหน่าย (บาท)": Number(item.OutboundAmount).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
        }),
        "แตก/ชำรุด (โด๊ส)": item.DisposalDoses,
        "แตก/ชำรุด (บาท)": Number(item.DisposalAmount).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
        }),
        "ยอดยกไป (โด๊ส)": item.EndingBalanceDoses,
        "ยอดยกไป (บาท)": Number(item.EndingBalanceAmount).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
        }),
        "หมายเหตุ": item.Remark || "-",
      })),
      {},
      {/*
      {
        "ชื่อวัคซีน": "คณะกรรมการตามคำสั่งที่" + (committeeData?.CMOrderNo || "...") + "ได้ตรวจนับวัคซีนคงเหลือแล้ว มียอดคงเหลือถูกต้องตรงกับยอดคงเหลือในรายงาน ผช.37",
      },
      {
        "ชื่อวัคซีน": "ลงชื่อ................................ประธานกรรมการ",
      },
      {
        "ชื่อวัคซีน": "("+ (committeeData?.CMName01 || "...") + ")",
      },
      {
        "ชื่อวัคซีน": "ลงชื่อ................................กรรมการ",
      },
      {
        "ชื่อวัคซีน": "(" + (committeeData?.CMName02 || "...") + ")",
      },
      {
        "ชื่อวัคซีน": "ลงชื่อ................................กรรมการ",
      },
      {
        "ชื่อวัคซีน": "(" + (committeeData?.CMName03 || "...") + ")",
      },
      {
        "ชื่อวัคซีน": "ลงนาม................................",
      },
      {
        "ชื่อวัคซีน": "(" + (committeeData?.CMName05 || "...") + ")",
      },
      {
        "ชื่อวัคซีน": "ตำแหน่ง" + committeeData?.CMPosition05 || "...",
      },
      {
        "ชื่อวัคซีน": "ลงวันที่................................",
      },
      */}
    ];
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MonthlyVaccineSummary");
    XLSX.writeFile(wb, "MonthlyVaccineSummary.xlsx");
  };

  
  const drawMultilineText = (doc, lines, x, y, lineHeight = 6) => {
    lines.forEach((line, index) => {
      doc.text(line, x, y + index * lineHeight);
    });
  };

  // Export to PDF
  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      //doc.addFileToVFS("Sarabun-Regular.ttf", fontBase64);
      //doc.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      doc.addFileToVFS("THSarabunNew.ttf", fontBase64);
      doc.addFont("THSarabunNew.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("เงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่าย", 14, 10);
    doc.text("ผช.37", 270, 10);
    doc.text("รายงานการเบิกจ่ายชีวภัณฑ์", 14, 16);
    doc.text("เพื่อจำหน่าย", 120, 16);
    doc.setFontSize(14);
    doc.text(
      `ประจำเดือน: ${new Date(filterData.MonthYear).toLocaleDateString("th-TH", {
        month: "long",
        year: "numeric",
      })}`,
      14,
      22
    );
    doc.text(
      `${
        warehouses.find((w) => w.WarehouseID === filterData.WarehouseID)
          ?.WarehouseName || "ไม่ระบุ"
      }`,
      200,
      22
    );

    // Calculate totals
    const totals = reportData.reduce(
      (acc, item) => {
        acc.CarryForwardDoses += item.CarryForwardDoses || 0;
        acc.CarryForwardAmount += item.CarryForwardAmount || 0;
        acc.InboundDoses += item.InboundDoses || 0;
        acc.InboundAmount += item.InboundAmount || 0;
        acc.OutboundDoses += item.OutboundDoses || 0;
        acc.OutboundAmount += item.OutboundAmount || 0;
        acc.DisposalDoses += item.DisposalDoses || 0;
        acc.DisposalAmount += item.DisposalAmount || 0;
        acc.EndingBalanceDoses += item.EndingBalanceDoses || 0;
        acc.EndingBalanceAmount += item.EndingBalanceAmount || 0;
        return acc;
      },
      {
        CarryForwardDoses: 0,
        CarryForwardAmount: 0,
        InboundDoses: 0,
        InboundAmount: 0,
        OutboundDoses: 0,
        OutboundAmount: 0,
        DisposalDoses: 0,
        DisposalAmount: 0,
        EndingBalanceDoses: 0,
        EndingBalanceAmount: 0,
      }
    );
    
    // Prepare table body with total row
    const body = [
      ...reportData.map((item) => [
      //item.VaccineID,
        item.VaccineNameTH,
        formatNumber(item.PricePerDose, { minimumFractionDigits: 2 }),
        formatNumber(item.CarryForwardDoses),
        formatNumber(item.CarryForwardAmount, { minimumFractionDigits: 2 }),
        formatNumber(item.InboundDoses),
        formatNumber(item.InboundAmount, { minimumFractionDigits: 2 }),
        formatNumber(item.OutboundDoses),
        formatNumber(item.OutboundAmount, { minimumFractionDigits: 2 }),
        formatNumber(item.DisposalDoses),
        formatNumber(item.DisposalAmount, { minimumFractionDigits: 2 }),
        formatNumber(item.EndingBalanceDoses),
        formatNumber(item.EndingBalanceAmount, { minimumFractionDigits: 2 }),
        item.Remark || "-",
      ]),
      [
        //"",
        "ยอดรวม",
        "",
        formatNumber(totals.CarryForwardDoses),
        formatNumber(totals.CarryForwardAmount, { minimumFractionDigits: 2 }),
        formatNumber(totals.InboundDoses),
        formatNumber(totals.InboundAmount, { minimumFractionDigits: 2 }),
        formatNumber(totals.OutboundDoses),
        formatNumber(totals.OutboundAmount, { minimumFractionDigits: 2 }),
        formatNumber(totals.DisposalDoses),
        formatNumber(totals.DisposalAmount, { minimumFractionDigits: 2 }),
        formatNumber(totals.EndingBalanceDoses),
        formatNumber(totals.EndingBalanceAmount, { minimumFractionDigits: 2 }),
        "-",
      ],
    ];

    autoTable(doc, {
      startY: 24,
      head: [
        [
        //{ content: "รหัสชีวภัณฑ์", rowSpan: 2 },
          { content: "ชื่อชีวภัณฑ์", rowSpan: 2 },
          { content: "ราคาต่อโด๊ส", rowSpan: 2 },
          { content: "ยอดยกมาจากเดือนก่อน", colSpan: 2 },
          { content: "รับจากปศุสัตว์เขต", colSpan: 2 },
          { content: "ขายไป/จำหน่าย", colSpan: 2 },
          { content: "แตก, ชำรุด, ทำลาย, ส่งคืน", colSpan: 2 },
          { content: "ยอดยกไปเดือนหน้า", colSpan: 2 },
          { content: "หมายเหตุ", rowSpan: 2 },
        ],
        [
          "โด๊ส",
          "จำนวนเงิน",
          "โด๊ส",
          "จำนวนเงิน",
          "โด๊ส",
          "จำนวนเงิน",
          "โด๊ส",
          "จำนวนเงิน",
          "โด๊ส",
          "จำนวนเงิน",
        ],
      ],
      body,
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 11,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: [255, 255, 255],
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        halign: "center",
        fillColor: [211, 211, 211],
        textColor: "black",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
        11: { halign: "right" },
        12: { halign: "right" },
      },
    });

    // Add footer text with committee signatures
    const finalY = doc.lastAutoTable.finalY || 34;
    doc.setFontSize(11);
    drawMultilineText(
      doc,
        [
          "หมายเหตุ : เอกสารประกอบงบที่ส่งมาด้วย",
          `1. หลักฐานการนำส่งเงินค่าจำหน่ายวัคซีน จำนวน............ชุด รวมเป็นเงิน ${Number(
            totals.OutboundAmount
          ).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท`,
          `2. ใบเสร็จรับเงิน (ฉบับสีชมพู) ...........ฉบับ เป็นเงิน ${Number(
            totals.OutboundAmount
          ).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท`,
          "3. เงินส่งขาด / ส่งเกิน จำนวน .....................บาท เนื่องจาก.....................",
          "4. หลักฐานการขออนุมัติจำหน่ายบัญชีวัคซีนประจำเดือนที่ส่งรายงาน (แตก, ชำรุด, ทำลาย)",
          "5. กรณีมี แตก, ชำรุด, ทำลาย, ส่งคืน ให้ระบุในช่องหมายเหตุ แจ้งว่ามี แตก, ชำรุด, ทำลาย, ส่งคืน เท่าไหร่",
          "",
        ],
        14,
        finalY + 5, 
        5 // line height
    );
    
    drawMultilineText(
      doc,
        [
          `คณะกรรมการตามคำสั่งที่ ${committeeData?.CMOrderNo || "......"} ได้ตรวจนับวัคซีนคงเหลือแล้ว`,
          `มียอดคงเหลือถูกต้องตรงกับยอดคงเหลือในรายงาน ผช.37`,
          ``,
          `ลงชื่อ.............................................ประธานกรรมการ`,
          `        (${committeeData?.CMName01 || ".............................."})`,
          ``,
          ``,
          `ลงชื่อ.............................................กรรมการ          ลงชื่อ.............................................กรรมการ`,
          `        (${committeeData?.CMName02 || ".............................."})                    (${
            committeeData?.CMName03 || ".............................."
          })`,
        ],
        150,
        finalY + 5,
        5 // line height
    );
    
    drawMultilineText(
      doc,
        [
          ``,
          `ลงนาม.............................................`,
          `       (${committeeData?.CMName05 || ".............................."})`,
          `ตำแหน่ง ${committeeData?.CMPosition05 || ".............................."}`,
          `ลงวันที่.........................................`,
        ],
        240,
        finalY + 5,
        5
    );

    doc.save("MonthlyVaccineSummary.pdf");
  };

  // Print table
  const printTable = () => {
    
    // Calculate totals
    const totals = reportData.reduce(
      (acc, item) => {
        acc.CarryForwardDoses += item.CarryForwardDoses || 0;
        acc.CarryForwardAmount += item.CarryForwardAmount || 0;
        acc.InboundDoses += item.InboundDoses || 0;
        acc.InboundAmount += item.InboundAmount || 0;
        acc.OutboundDoses += item.OutboundDoses || 0;
        acc.OutboundAmount += item.OutboundAmount || 0;
        acc.DisposalDoses += item.DisposalDoses || 0;
        acc.DisposalAmount += item.DisposalAmount || 0;
        acc.EndingBalanceDoses += item.EndingBalanceDoses || 0;
        acc.EndingBalanceAmount += item.EndingBalanceAmount || 0;
        return acc;
      },
      {
        CarryForwardDoses: 0,
        CarryForwardAmount: 0,
        InboundDoses: 0,
        InboundAmount: 0,
        OutboundDoses: 0,
        OutboundAmount: 0,
        DisposalDoses: 0,
        DisposalAmount: 0,
        EndingBalanceDoses: 0,
        EndingBalanceAmount: 0,
      }
    );
  
    const printContent = `
      <html>
        <head>
          <title>Monthly Vaccine Summary</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .signature-section { margin-top: 20px; }
            .signature-section p { margin: 5px 0; }
          </style>
        </head>
        <body>
          <h3>เงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่าย</h3>
          <h3>รายงานงบการเบิกจ่ายชีวผลิตภัณฑ์ เพื่อจำหน่าย</h3>
          <p>เดือน: ${new Date(filterData.MonthYear).toLocaleDateString("th-TH", {
            month: "long",
            year: "numeric",
          })}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          ${
            warehouses.find((w) => w.WarehouseID === filterData.WarehouseID)
              ?.WarehouseName || "ไม่ระบุ"
          }</p>
          <table>
            <thead>
              <tr>
                <th rowspan=2>ชื่อวัคซีน</th>
                <th rowspan=2>ราคาต่อโด๊ส</th>
                <th colspan=2>ยอดยกมาจากเดือนก่อน</th>
                <th colspan=2>รับจากปศุสัตว์เขต</th>
                <th colspan=2>ขายไป/จำหน่าย</th>
                <th colspan=2>แตก, ชำรุด, ทำลาย, ส่งคืน</th>
                <th colspan=2>ยอดยกไปเดือนหน้า</th>
                <th rowspan=2>หมายเหตุ</th>
              </tr>
              <tr>
                <th>โด๊ส</th>
                <th>จำนวนเงิน</th>
                <th>โด๊ส</th>
                <th>จำนวนเงิน</th>
                <th>โด๊ส</th>
                <th>จำนวนเงิน</th>
                <th>โด๊ส</th>
                <th>จำนวนเงิน</th>
                <th>โด๊ส</th>
                <th>จำนวนเงิน</th>
              </tr>
            </thead>
            <tbody>
              ${reportData
                .map(
                  (item) => `
                  <tr>
                    <td>${item.VaccineNameTH}</td>
                    <td>${formatNumber(item.PricePerDose, { minimumFractionDigits: 2 })}</td>
                    <td>${formatNumber(item.CarryForwardDoses)}</td>
                    <td>${formatNumber(item.CarryForwardAmount, { minimumFractionDigits: 2 })}</td>
                    <td>${formatNumber(item.InboundDoses)}</td>
                    <td>${formatNumber(item.InboundAmount, { minimumFractionDigits: 2 })}</td>
                    <td>${formatNumber(item.OutboundDoses)}</td>
                    <td>${formatNumber(item.OutboundAmount, { minimumFractionDigits: 2 })}</td>
                    <td>${formatNumber(item.DisposalDoses)}</td>
                    <td>${formatNumber(item.DisposalAmount, { minimumFractionDigits: 2 })}</td>
                    <td>${formatNumber(item.EndingBalanceDoses, { minimumFractionDigits: 2 })}</td>
                    <td>${formatNumber(item.EndingBalanceAmount, { minimumFractionDigits: 2 })}</td>
                    <td>${item.Remark || "-"}</td>
                  </tr>
                  `
                )
                .join("")}
              <tr>
                <td>ยอดรวม</td>
                <td></td>
                <td>${formatNumber(totals.CarryForwardDoses)}</td>
                <td>${formatNumber(totals.CarryForwardAmount, { minimumFractionDigits: 2 })}</td>
                <td>${formatNumber(totals.InboundDoses)}</td>
                <td>${formatNumber(totals.InboundAmount, { minimumFractionDigits: 2 })}</td>
                <td>${formatNumber(totals.OutboundDoses)}</td>
                <td>${formatNumber(totals.OutboundAmount, { minimumFractionDigits: 2 })}</td>
                <td>${formatNumber(totals.DisposalDoses)}</td>
                <td>${formatNumber(totals.DisposalAmount, { minimumFractionDigits: 2 })}</td>
                <td>${formatNumber(totals.EndingBalanceDoses)}</td>
                <td>${formatNumber(totals.EndingBalanceAmount).toLocaleString("th-TH", {
                  minimumFractionDigits: 2,
                })}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <div class="signature-section">
            <p>หมายเหตุ : เอกสารประกอบงบที่ส่งมาด้วย</p>
            <p1. หลักฐานการนำส่งเงินค่าจำหน่ายวัคซีน จำนวน............ชุด รวมเป็นเงิน ${Number(totals.OutboundAmount).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท</p>
            <p>2. ใบเสร็จรับเงิน (ฉบับสีชมพู) ...........ฉบับ เป็นเงิน ${Number(totals.OutboundAmount).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท</p>
            <p>3. เงินส่งขาด / ส่งเกิน จำนวน .....................บาท เนื่องจาก.....................</p>
            <p>4. หลักฐานการขออนุมัติจำหน่ายบัญชีวัคซีนประจำเดือนที่ส่งรายงาน (แตก, ชำรุด, ทำลาย)</p>
            <p>5. กรณีมี แตก, ชำรุด, ทำลาย, ส่งคืน ให้ระบุในช่องหมายเหตุ แจ้งว่ามี แตก, ชำรุด, ทำลาย, ส่งคืน เท่าไหร่</p>
          </div>
          <div class="signature-section">
            <p>คณะกรรมการตามคำสั่งที่ ${committeeData?.CMOrderNo || "..."} ได้ตรวจนับวัคซีนคงเหลือแล้ว</p>
            <p>มียอดคงเหลือถูกต้องตรงกับยอดคงเหลือในรายงาน ผช.37</p>
            <p>&nbsp;</p>
            <p>ลงชื่อ.............................................ประธานกรรมการ</p>
            <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMName01 || ".............................."})</p>
            <p>&nbsp;</p>
            <p>ลงชื่อ.............................................กรรมการ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ลงชื่อ.............................................กรรมการ</p>
            <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMName02 || ".............................."}) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${
              committeeData?.CMName03 || ".............................."
            })</p>
            <p>&nbsp;</p>
            <p>ลงนาม.............................................</p>
            <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMName05 || ".............................."})</p>
            <p>ตำแหน่ง ${committeeData?.CMPosition05 || ".............................."}</p>
            <p>ลงวันที่...............................</p>
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานเงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่าย - งบการเบิกจ่ายชีวภัณฑ์ (ผช.37)" />

      {/* Filter Form */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลวัคซีน"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                คลังวัคซีน
              </FormLabel>
              <Select
                name="WarehouseID"
                value={filterData.WarehouseID}
                onChange={handleInputChange}
                placeholder={warehouses.length === 0 ? "ไม่มีคลังวัคซีน" : "เลือกคลังวัคซีน"}
                colorScheme="iBlue"
              >
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl><FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                เดือน/ปี
              </FormLabel>
              <Select
                name="MonthYear"
                value={filterData.MonthYear}
                onChange={(e) =>
                  setFilterData({ ...filterData, MonthYear: e.target.value })
                }
                placeholder="เลือกเดือน/ปี"
                colorScheme="iBlue"
              >
                {generateMonthYearOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    WarehouseID: warehouses.length > 0 ? warehouses[0].WarehouseID : "",
                    MonthYear: "",
                  })
                }
              />
              <Search onClick={fetchReportData} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Table */}
      {reportData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="เงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่าย - งบการเบิกจ่ายชีวภัณฑ์ (ผช.37)"
            bgColor="iBlue.600"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={reportData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
              noDataComponent="ไม่มีข้อมูลแสดงผล"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VaccineFundingMonthlySummary;